
import { useState, useEffect } from 'react';

interface UseAnimatedPlaceholderProps {
  placeholderWords: string[];
  isFocused: boolean;
  hasValue: boolean;
}

export function useAnimatedPlaceholder({
  placeholderWords,
  isFocused,
  hasValue
}: UseAnimatedPlaceholderProps) {
  const [currentWord, setCurrentWord] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  
  // Handle the typing animation effect for the placeholder
  useEffect(() => {
    const animatePlaceholder = () => {
      const currentTarget = placeholderWords[currentWordIndex];
      const isComplete = currentWord === currentTarget;
      const isEmpty = currentWord === '';

      if (!isDeleting && !isComplete) {
        // Still typing out the current word
        setCurrentWord(currentTarget.substring(0, currentWord.length + 1));
        setTypingSpeed(150); // Normal typing speed
      } else if (!isDeleting && isComplete) {
        // Finished typing, pause before deleting
        setIsDeleting(true);
        setTypingSpeed(1000); // Pause at the end of the word
      } else if (isDeleting && !isEmpty) {
        // Deleting the current word
        setCurrentWord(currentWord.substring(0, currentWord.length - 1));
        setTypingSpeed(80); // Delete faster than typing
      } else if (isDeleting && isEmpty) {
        // Move to the next word after deleting
        setIsDeleting(false);
        setCurrentWordIndex((currentWordIndex + 1) % placeholderWords.length);
        setTypingSpeed(500); // Pause before starting the next word
      }
    };

    // Don't animate if the input is focused or has a value
    if (!isFocused && !hasValue) {
      const timeout = setTimeout(animatePlaceholder, typingSpeed);
      return () => clearTimeout(timeout);
    }
  }, [currentWord, currentWordIndex, isDeleting, typingSpeed, isFocused, hasValue, placeholderWords]);

  return {
    currentWord
  };
}
